import { useFonts, Inter_400Regular, Inter_500Medium, Inter_600SemiBold, Inter_700Bold } from '@expo-google-fonts/inter';
import { JetBrainsMono_400Regular, JetBrainsMono_500Medium } from '@expo-google-fonts/jetbrains-mono';
import { Sora_600SemiBold, Sora_800ExtraBold } from '@expo-google-fonts/sora';
import * as Notifications from 'expo-notifications';
import { router, Stack } from 'expo-router';
import * as SplashScreen from 'expo-splash-screen';
import { useEffect, useRef, useState } from 'react';
import { AccessibilityInfo, Platform, Pressable, View } from 'react-native';
import { SafeAreaProvider } from 'react-native-safe-area-context';

import { space } from '@bero/tokens';
import { GateSplash } from '@/components/brand';
import { DesktopFrame } from '@/components/shell/DesktopShell';
import { clearActingSociety, useActingSociety } from '@/lib/acting-society';
import '@/lib/society-header'; // registers the single X-Bero-Society coordinator
import { SessionProvider } from '@/lib/session';
import { I18nProvider, useT } from '@/i18n';
import type { DictKey } from '@/i18n/en';
import { BeroThemeProvider, useTheme } from '@/theme';
import { AppText } from '@/ui';

SplashScreen.preventAutoHideAsync();

function RootStack() {
  const { colors } = useTheme();
  return (
    <Stack
      screenOptions={{
        headerShown: false,
        contentStyle: { backgroundColor: colors.canvas0 },
      }}
    />
  );
}

const SPLASH_PATHS = ['/', '/login', '/home'];

/** Web only: the pathname the browser actually cold-loaded, captured once
 * before any client-side navigation can change it. Native has no equivalent
 * "arbitrary deep link on cold start" concept in the same sense, so it keeps
 * the previous always-play behavior. */
function initialWebPath(): string | null {
  if (Platform.OS !== 'web' || typeof window === 'undefined') return null;
  return window.location.pathname;
}

/**
 * O1 "Gate draws" intro after the native splash, once per cold start. On web,
 * only played for cold loads landing on '/', '/login' or '/home' — every
 * other deep link (a shared poll/event/issue URL, a bookmark, a refresh)
 * skips straight to content instead of holding on the brand mark for the
 * ~4.3s animation. On native, a launch FROM A NOTIFICATION also skips it —
 * someone tapping "Visitor at the gate" needs the approval screen, not four
 * seconds of brand animation (user report 2026-07-18).
 */
function SplashGate({ children, notificationLaunch }: {
  children: React.ReactNode;
  /** null = still checking how the app was launched; true = notification tap. */
  notificationLaunch: boolean | null;
}) {
  const [done, setDone] = useState(false);
  const [reduced, setReduced] = useState<boolean | null>(null);
  const [initialPath] = useState(initialWebPath);

  useEffect(() => {
    AccessibilityInfo.isReduceMotionEnabled()
      .then((v) => setReduced(v))
      .catch(() => setReduced(false));
  }, []);

  const eligibleForSplash = Platform.OS !== 'web' || (initialPath !== null && SPLASH_PATHS.includes(initialPath));
  if (!eligibleForSplash) return <>{children}</>;

  if (done || reduced === true || notificationLaunch === true) return <>{children}</>;
  if (reduced === null || notificationLaunch === null) return null;
  return <GateSplash onDone={() => setDone(true)} />;
}

/**
 * Amber banner shown above the whole app whenever a Bero platform staffer is
 * "acting" as a society (entered via /platform/society/[id] to operate its
 * ordinary admin console on its behalf) — a constant, impossible-to-miss
 * reminder of whose data is on screen, plus a one-tap Exit back to the
 * console. Web only: the acting flow is a web-console feature (see
 * lib/acting-society.ts) and gating here avoids double safe-area insets with
 * each screen's own TopBar, which isn't a concern this banner needs to solve
 * on native.
 */
/** acting-society.ts stores the raw platform-role enum (set from
 * usePlatformRole() in society/[id].tsx's enterSociety()) — humanize it with
 * the same plat_role_* keys members.tsx already uses, instead of interpolating
 * "SUPER_ADMIN"/"SUPPORT" straight into the banner string. */
const ACTING_ROLE_LABEL_KEY: Record<string, DictKey> = {
  SUPER_ADMIN: 'plat_role_super_admin',
  SALES: 'plat_role_sales',
  SUPPORT: 'plat_role_support',
  MARKETING: 'plat_role_marketing',
};

function ActingBanner() {
  const { colors } = useTheme();
  const { t } = useT();
  const acting = useActingSociety();
  if (Platform.OS !== 'web' || !acting) return null;
  const roleLabel = ACTING_ROLE_LABEL_KEY[acting.role] ? t(ACTING_ROLE_LABEL_KEY[acting.role]) : acting.role;
  return (
    <View
      style={{
        backgroundColor: colors.accent100,
        flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between',
        gap: space.s3, paddingVertical: 8, paddingHorizontal: space.s4,
      }}
    >
      <AppText variant="bodySm" weight="500" style={{ color: colors.accent700, flex: 1 }}>
        {t('plat_acting_banner', { society: acting.name, role: roleLabel })}
      </AppText>
      <Pressable
        accessibilityRole="button"
        onPress={() => { clearActingSociety(); router.replace('/platform'); }}
        hitSlop={8}
      >
        <AppText variant="bodySm" weight="600" style={{ color: colors.accent700 }}>
          {t('plat_acting_exit')}
        </AppText>
      </Pressable>
    </View>
  );
}

export default function RootLayout() {
  const [loaded] = useFonts({
    Inter_400Regular, Inter_500Medium, Inter_600SemiBold, Inter_700Bold,
    JetBrainsMono_400Regular, JetBrainsMono_500Medium,
    Sora_600SemiBold, Sora_800ExtraBold,
  });

  useEffect(() => {
    if (loaded) SplashScreen.hideAsync();
  }, [loaded]);

  // How did this cold start happen? A notification tap that LAUNCHED the app
  // never reaches the live listener below — it has to be read back explicitly,
  // or the tap is silently lost and the user lands on home none the wiser
  // (user report 2026-07-18: approval notification → home, tiny banner).
  const [notificationLaunch, setNotificationLaunch] = useState<boolean | null>(
    Platform.OS === 'web' ? false : null,
  );
  const handledResponseId = useRef<string | null>(null);
  useEffect(() => {
    if (Platform.OS === 'web') return;
    Notifications.getLastNotificationResponseAsync()
      .then((response) => {
        const url = response?.notification.request.content.data?.url;
        if (typeof url === 'string' && url.startsWith('/')) {
          handledResponseId.current = response?.notification.request.identifier ?? null;
          setNotificationLaunch(true);
          // Give the root stack one tick to mount before navigating.
          setTimeout(() => router.push(url as never), 80);
        } else {
          setNotificationLaunch(false);
        }
      })
      .catch(() => setNotificationLaunch(false));
  }, []);

  // Tapping a notification while the app is alive routes to its data.url.
  useEffect(() => {
    if (Platform.OS === 'web') return;
    const sub = Notifications.addNotificationResponseReceivedListener((response) => {
      if (response.notification.request.identifier === handledResponseId.current) return;
      const url = response.notification.request.content.data?.url;
      if (typeof url === 'string' && url.startsWith('/')) {
        router.push(url as never);
      }
    });
    return () => sub.remove();
  }, []);

  // Visitor-approval pushes RECEIVED while the app is open take over the
  // screen immediately — no waiting for a banner tap (user rule 2026-07-20:
  // a quiet banner is invisible to most users; MyGate-style center takeover).
  // The tone still plays via the notification handler's shouldPlaySound.
  useEffect(() => {
    if (Platform.OS === 'web') return;
    const sub = Notifications.addNotificationReceivedListener((notification) => {
      const url = notification.request.content.data?.url;
      if (typeof url === 'string' && url.startsWith('/visitor-approval')) {
        router.push(url as never);
      }
    });
    return () => sub.remove();
  }, []);

  if (!loaded) return null;

  return (
    <SafeAreaProvider>
      <BeroThemeProvider>
        <SessionProvider>
          <I18nProvider>
            <SplashGate notificationLaunch={notificationLaunch}>
              <ActingBanner />
              <DesktopFrame>
                <RootStack />
              </DesktopFrame>
            </SplashGate>
          </I18nProvider>
        </SessionProvider>
      </BeroThemeProvider>
    </SafeAreaProvider>
  );
}
